import { ArrowRight, Sparkles } from "lucide-react";
import { ListingLink } from "./ListingLink";
import { resolveImg } from "@/lib/marketplaceTrack";

const heroLink = (url, label) => ({ id: `hero-${label}`, destination_url: url });

export function MarketHero({ hero, spotlight }) {
  if (!hero) return <div className="aspect-video max-h-[760px] min-h-[560px] bg-[#0A0A0A]" />;
  return (
    <section data-testid="mkt-hero" className="relative overflow-hidden ambient-grain">
      <div className="relative w-full min-h-[600px] lg:min-h-0 lg:aspect-video lg:max-h-[min(780px,86vh)]">
        {hero.image_url && <img src={resolveImg(hero.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55 mkt-kenburns" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/75 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/30" />
        <div className="relative h-full max-w-[1360px] mx-auto px-6 lg:px-10 flex items-end lg:items-center pb-16 pt-28 lg:py-0">
          <div className="grid lg:grid-cols-12 gap-10 w-full items-end">
            <div className="lg:col-span-7 mkt-rise">
              {hero.eyebrow && <div className="label-eyebrow flex items-center gap-2 mb-6"><Sparkles size={14} /> {hero.eyebrow}</div>}
              <h1 data-testid="mkt-hero-title" className="font-serif-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.02] tracking-tight">
                {hero.title} {hero.highlight && <span className="italic text-[#D4AF37]">{hero.highlight}</span>}
              </h1>
              {hero.subtitle && <p className="mt-7 text-base lg:text-lg text-neutral-300 max-w-xl leading-relaxed">{hero.subtitle}</p>}
              <div className="mt-10 flex flex-wrap gap-4">
                {hero.primary_label && (
                  <ListingLink listing={heroLink(hero.primary_url, "primary")} testid="mkt-hero-primary"
                    className="btn-gold px-7 py-3.5 rounded inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold">
                    {hero.primary_label} <ArrowRight size={15} />
                  </ListingLink>
                )}
                {hero.secondary_label && (
                  <ListingLink listing={heroLink(hero.secondary_url, "secondary")} testid="mkt-hero-secondary"
                    className="btn-ghost-gold px-7 py-3.5 rounded inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold">
                    {hero.secondary_label}
                  </ListingLink>
                )}
              </div>
            </div>
            {spotlight && <Spotlight listing={spotlight} />}
          </div>
        </div>
      </div>
    </section>
  );
}

function Spotlight({ listing }) {
  return (
    <ListingLink listing={listing} testid="mkt-hero-spotlight"
      className="hidden lg:block lg:col-span-4 lg:col-start-9 group rounded-lg border border-white/10 bg-black/50 backdrop-blur-xl overflow-hidden hover:border-[#D4AF37]/60 transition-colors mkt-rise" >
      <div className="aspect-video overflow-hidden">
        <img src={resolveImg(listing.image_url)} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      <div className="p-5">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">Spotlight</div>
        <div className="font-serif-display text-2xl text-white mt-1">{listing.title}</div>
        <div className="flex justify-between items-center mt-3 text-sm">
          <span className="text-neutral-400">{listing.price_label}</span>
          <span className="text-xs tracking-widest uppercase text-neutral-200 group-hover:text-[#D4AF37] inline-flex items-center gap-1">{listing.cta_label} <ArrowRight size={13} /></span>
        </div>
      </div>
    </ListingLink>
  );
}
